/**
 * 闪卡卡组 → reveal.js 独立 HTML 导出
 * 参考微课导出管线（components/microcourse/export/generateHTML.ts），
 * 每张卡一个 slide，fragment 逐步揭示提示与答案。
 */

import type { CardItem } from "@/api/flashcards"

export interface DeckExportOptions {
  /** reveal.js 内置主题 id（white/black/beige/sky/...），默认 white */
  theme?: string
  /** 副标题（如学科） */
  subject?: string | null
}

export function generateDeckHTML(
  deckName: string,
  cards: CardItem[],
  options: DeckExportOptions = {},
): string {
  const theme = options.theme || "white"

  const coverSection = `  <section>
    <h1>${escapeHtml(deckName)}</h1>
    <p class="deck-cover-meta">${escapeHtml(options.subject ?? "")}${options.subject ? " · " : ""}共 ${cards.length} 张卡片</p>
    <p class="deck-cover-tip">按空格或方向键逐张翻阅，每页再次按键揭示答案</p>
  </section>`

  const cardSections = cards
    .map((card, index) => cardToSection(card, index))
    .join("\n")

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(deckName)}</title>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/reveal.js@4/dist/reveal.css" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/reveal.js@4/dist/theme/${theme}.css" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@latest/dist/katex.min.css" />
  <style>
    .reveal .slides { text-align: center; }
    .reveal .slides section { padding: 20px 40px; }
    .card-index { font-size: 0.5em; color: rgba(128, 128, 128, 0.8); letter-spacing: 0.1em; margin-bottom: 0.6em; }
    .card-tags { font-size: 0.45em; color: #64748b; margin-top: 0.8em; }
    .card-tags span { display: inline-block; border: 1px solid currentColor; border-radius: 999px; padding: 0.1em 0.7em; margin: 0 0.2em; }
    .card-front { font-size: 1.15em; line-height: 1.6; margin: 1em auto; max-width: 42em; white-space: pre-wrap; }
    .card-hint { font-size: 0.6em; color: #94a3b8; }
    .card-back {
      display: inline-block; text-align: left; max-width: 42em; margin-top: 0.8em;
      background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.35);
      border-radius: 12px; padding: 0.8em 1.2em; font-size: 0.9em; line-height: 1.6;
      white-space: pre-wrap;
    }
    .deck-cover-meta { color: #94a3b8; font-size: 0.8em; }
    .deck-cover-tip { color: #94a3b8; font-size: 0.55em; margin-top: 2em; }
  </style>
</head>
<body>
  <div class="reveal">
    <div class="slides">
${coverSection}
${cardSections}
    </div>
  </div>
  <script src="https://cdn.jsdelivr.net/npm/reveal.js@4/dist/reveal.js"></script>
  <script src="https://cdn.jsdelivr.net/npm/reveal.js@4/plugin/math/math.js"></script>
  <script>
    Reveal.initialize({
      hash: true,
      slideNumber: 'c/t',
      transition: 'slide',
      math: { katex: { version: 'latest' } },
      plugins: [RevealMath.KaTeX],
    });
  </script>
</body>
</html>`
}

function cardToSection(card: CardItem, index: number): string {
  const tags = (card.tags ?? [])
    .map((t) => `<span>${escapeHtml(t)}</span>`)
    .join("")
  const tagsHTML = tags ? `<div class="card-tags">${tags}</div>` : ""
  const hintHTML = card.hint
    ? `<p class="fragment card-hint">提示：${escapeHtml(card.hint)}</p>`
    : ""

  return `  <section>
    <div class="card-index">第 ${index + 1} 张</div>
    <div class="card-front">${escapeHtml(card.front)}</div>
${hintHTML}
    <div class="fragment card-back">${escapeHtml(card.back)}</div>
${tagsHTML}
  </section>`
}

export function downloadDeckHTML(
  deckName: string,
  cards: CardItem[],
  options: DeckExportOptions = {},
): void {
  const html = generateDeckHTML(deckName, cards, options)
  const blob = new Blob([html], { type: "text/html;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `${sanitizeFilename(deckName)}.html`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function sanitizeFilename(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, "_").slice(0, 60) || "flashcards"
}
